import type { PortalConfig } from '../config/schema.js';
import type { LeadField } from '../records/fields.js';

/**
 * Lead rules shared by the web app and the API (brief sections 2, 3 and
 * 6.8): what keeps a lead out of HubSpot, the follow-up tasks it carries,
 * and the suggestions of the lead form (deal name, deal market, stage).
 */

export const LEAD_FIELD_LABELS: Record<LeadField, string> = {
  interest: 'Interest',
  role: 'Role in the project',
  pain: 'Pain',
  applications: 'Operating market(s)',
  country: 'Company country',
  company_name: 'Company name',
  company_hubspot_id: 'HubSpot company',
  event_id: 'Event ID',
  deal_name: 'Deal name',
  installation_country: 'Installation country',
  owner_user_id: 'Deal owner',
  stage_id: 'Deal stage',
  deal_market: 'Deal market',
  product_fit: 'Product fit',
  qualification: 'Looks qualified or not',
  disqualified_why: 'Why not qualified',
  disqualified_when: 'When this can change',
  project_call_date: 'Project Call',
  note: 'Note',
  where_from: 'Where from',
};

/** The two outcomes of the Discovery Capture (New Clients qualification flow). */
export const QUALIFICATION_LABELS: Record<'qualified' | 'disqualified', string> = {
  qualified: 'Looks qualified',
  disqualified: 'Not qualified',
};

export interface LeadBlocker {
  field: LeadField;
  label: string;
}

/** A lead as stored or as a list row: any record with the lead fields. */
type LeadLike = Readonly<Record<string, unknown>>;

const given = (value: unknown) =>
  Array.isArray(value)
    ? value.length > 0
    : typeof value === 'string'
      ? value.trim() !== ''
      : value != null;

/**
 * What keeps a lead out of HubSpot (brief section 2, rule 9): the company,
 * the four answers, the full Discovery Capture, a deal name and a deal
 * market. Interest is no longer asked (decision of Nov 2, 2026). Qualified needs
 * the product fit; disqualified needs why and when. The Project Call date
 * never blocks: it becomes a HubSpot task. The deal stage follows the
 * qualification, so it is asked only once the lead is qualified or not.
 */
export function leadPushBlockers(lead: LeadLike): LeadBlocker[] {
  const fields: LeadField[] = [
    'company_name',
    'country',
    'applications',
    'role',
    'pain',
    'qualification',
  ];
  if (lead.qualification === 'disqualified') fields.push('disqualified_why', 'disqualified_when');
  else fields.push('product_fit');
  fields.push('installation_country', 'owner_user_id');
  if (given(lead.qualification)) fields.push('stage_id');
  fields.push('deal_name', 'deal_market');
  return fields
    .filter((field) => !given(lead[field]))
    .map((field) => ({ field, label: LEAD_FIELD_LABELS[field] }));
}

/** Follow-ups that become HubSpot tasks at the push (brief section 2, rule 12). */
export function leadTasks(lead: LeadLike): string[] {
  return lead.qualification === 'qualified' && !given(lead.project_call_date)
    ? ['Book the Project Call']
    : [];
}

/**
 * The deal name suggested: the company's name alone (decision of Nov 3,
 * 2026); the event ID stays in its own HubSpot property (wr_event_id).
 * `_eventCode` is kept for the callers, and ignored.
 */
export function suggestDealName(
  company: string | null | undefined,
  _eventCode?: string | null | undefined,
) {
  return (company ?? '').trim();
}

/**
 * The event ID suffix of a deal name the portal suggested before Nov 3,
 * 2026 ("Company - EVENTID"): the name without it, or null when the name
 * is not that suggestion.
 */
export function dealNameWithoutEventId(
  dealName: string | null | undefined,
  company: string | null | undefined,
  eventCode: string | null | undefined,
): string | null {
  const name = (dealName ?? '').trim();
  const firm = (company ?? '').trim();
  const code = (eventCode ?? '').trim();
  if (!name || !firm || !code) return null;
  return name === `${firm} - ${code}` ? firm : null;
}

/** "Last seen" in HubSpot: the event ID, else nothing (brief section 4, Lead Entry). */
export function lastSeenOf(eventCode: string | null | undefined): string {
  return eventCode ?? '';
}

type StageLists = Pick<PortalConfig['lists'], 'dealStagesFallback' | 'dealStageDefaults'>;

/** Qualified: MQLs; disqualified: Marketing nurturing (brief section 2, rule 8). */
export function defaultStage(qualification: unknown, lists: StageLists): string | null {
  if (qualification === 'qualified') return lists.dealStageDefaults.qualified;
  if (qualification === 'disqualified') return lists.dealStageDefaults.disqualified;
  return null;
}

export function stageLabel(id: unknown, lists: StageLists): string {
  if (typeof id !== 'string' || !id) return '';
  return lists.dealStagesFallback.find((s) => s.id === id)?.label ?? id;
}
